'use client';

import { useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCentrifugo } from '../contexts/CentrifugoContext';
import type { RecentInventoryItem } from '../services/generated/Api';

const CHANNEL_DROPS_FEED_SUFFIX = 'drops_feed';

const getDropsFeedChannel = (prefix: string): string => {
  return prefix ? `${prefix}_${CHANNEL_DROPS_FEED_SUFFIX}` : CHANNEL_DROPS_FEED_SUFFIX;
};

export const useTopDropsSubscription = () => {
  const { subscribe, isConnected, channelPrefix } = useCentrifugo();
  const queryClient = useQueryClient();

  const channelName = useMemo(
    () => getDropsFeedChannel(channelPrefix),
    [channelPrefix],
  );

  const handleNewDrop = useCallback(
    (data: unknown) => {
      const drop = data as RecentInventoryItem;

      // Update only recentDrops cache (with dedup by ID).
      // topDrops is price-sorted and must only come from the API.
      queryClient.setQueryData<RecentInventoryItem[]>(
        ['home', 'recentDrops'],
        (oldDrops = []) => {
          if (oldDrops.some(d => d.id === drop.id)) return oldDrops;
          return [drop, ...oldDrops.slice(0, 19)];
        },
      );
    },
    [queryClient],
  );

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = subscribe(channelName, handleNewDrop);

    return unsubscribe;
  }, [isConnected, channelName, subscribe, handleNewDrop]);

  return { isConnected };
};
